import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, formatMoney, formatTime12h, formatDateLong } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const Success = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const freeFlow = searchParams.get("free") === "1";
  const bookingId = searchParams.get("booking_id");
  const [state, setState] = useState({ status: freeFlow ? "polling" : "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    // Free flow: fetch booking directly, no Stripe polling
    if (freeFlow && bookingId) {
      api.get(`/bookings/${bookingId}`).then((r) => {
        setState({
          status: "success",
          data: { booking: r.data.booking, amount_total: 0, currency: "usd", free: true },
          error: null,
        });
      }).catch(() => {
        setState({ status: "error", data: null, error: "Could not load booking." });
      });
      return;
    }
    if (!sessionId) {
      setState({ status: "error", data: null, error: "No session id provided." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setState({ status: "success", data: res.data, error: null });
          return;
        }
        if (res.data.status === "expired") {
          setState({ status: "expired", data: res.data, error: null });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data: res.data, error: null });
          return;
        }
        attemptsRef.current += 1;
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", data: null, error: "Could not verify payment." });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24" data-testid="success-page">
      <div className="max-w-[900px] mx-auto px-6 sm:px-12 lg:px-16 text-center">
        {state.status === "polling" && (
          <div className="py-16" data-testid="success-polling">
            <Loader2 className="animate-spin text-[#B89B63] mx-auto" size={40} />
            <p className="font-serif text-3xl text-[#4F5D4F] mt-8">
              Confirming your session…
            </p>
            <p className="text-[#8C9A88] mt-3 text-sm tracking-wide">
              This usually takes just a few seconds.
            </p>
          </div>
        )}

        {state.status === "success" && (
          <div className="py-12" data-testid="success-confirmed">
            <CheckCircle2 className="text-[#B89B63] mx-auto" size={48} />
            <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mt-6">
              {state.data?.free ? "Your discovery call is scheduled" : "Your session is confirmed"}
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl text-[#4F5D4F] mt-6 leading-[1.05]">
              {state.data?.free ? (
                <>Talk soon,<br /><em className="italic text-[#B89B63]">brave one.</em></>
              ) : (
                <>See you soon,<br /><em className="italic text-[#B89B63]">brave one.</em></>
              )}
            </h1>
            <p className="text-lg text-[#4F5D4F]/80 mt-8 leading-relaxed font-light max-w-xl mx-auto">
              {state.data?.free
                ? "A Google Calendar invite is on its way to your inbox with our Google Meet link. There's no prep required — come exactly as you are."
                : "A confirmation note is on its way to your inbox. Jess will reach out within 24 hours with your video link and a short intake form to help you arrive grounded."}
            </p>

            {state.data?.booking && (
              <div className="mt-12 bg-white p-8 border border-[#D7B2A3]/50 max-w-lg mx-auto text-left">
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-[#8C9A88] tracking-eyebrow uppercase text-xs">Date</span>
                  <span className="text-[#4F5D4F]">
                    {formatDateLong(state.data.booking.session_date)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-[#8C9A88] tracking-eyebrow uppercase text-xs">Time</span>
                  <span className="text-[#4F5D4F]">
                    {formatTime12h(state.data.booking.session_time)} ET
                  </span>
                </div>
                {!state.data.free && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8C9A88] tracking-eyebrow uppercase text-xs">Paid</span>
                    <span className="text-[#4F5D4F]">
                      {formatMoney((state.data.amount_total || 0) / 100, state.data.currency)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <Link
              to="/"
              className="inline-block mt-12 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-8 py-4 text-xs tracking-eyebrow uppercase transition-colors"
              data-testid="success-home-button"
            >
              Return home
            </Link>
          </div>
        )}

        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <div className="py-16" data-testid="success-error">
            <XCircle className="text-[#8C9A88] mx-auto" size={40} />
            <p className="font-serif text-3xl text-[#4F5D4F] mt-8">
              {state.status === "expired"
                ? "This session expired."
                : state.status === "timeout"
                ? "Payment is taking longer than usual."
                : "Something went wrong."}
            </p>
            <p className="text-[#8C9A88] mt-3 text-sm">
              {state.status === "timeout"
                ? "Please check your email — you'll receive a confirmation if payment succeeded."
                : "Please reach out to hello@bravelyyou.co if anything was charged."}
            </p>
            <Link
              to="/booking"
              className="inline-block mt-8 border border-[#4F5D4F] text-[#4F5D4F] hover:bg-[#4F5D4F] hover:text-[#F6F1EA] px-8 py-4 text-xs tracking-eyebrow uppercase transition-colors"
            >
              Try again
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default Success;
